import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudDone as CloudDoneIcon,
  CheckCircle as CheckCircleIcon,
  CloudQueue as CloudQueueIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  VerifiedUser as VerifiedUserIcon,
  Verified as VerifiedIcon,
  Policy as PolicyIcon,
  GppGood as GppGoodIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  Accessibility as AccessibilityIcon,
  TrendingUp as TrendingUpIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';

const FedRAMPReady: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(2);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const urls = [
      '/capabilities/high-performance',
      '/capabilities/zero-trust-security',
      '/capabilities/fedramp-ready',
      '/capabilities/section-508-compliant',
      '/capabilities/scalable-architecture',
      '/capabilities/no-vendor-lock-in'
    ];
    navigate(urls[newValue]);
  };

  const benefits = [
    'Built on FedRAMP High authorized cloud infrastructure',
    'Continuous monitoring and compliance reporting',
    'Government-approved security controls (NIST 800-53)',
    'Automated compliance documentation and evidence collection',
    'Regular security assessments and penetration testing',
    'Dedicated FedRAMP compliance team and support'
  ];

  const capabilities = [
    {
      icon: <CloudQueueIcon sx={{ color: 'primary.main' }} />,
      title: 'Authorized Infrastructure',
      description: 'Hosted on AWS GovCloud with FedRAMP High authorization'
    },
    {
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
      title: 'Security Controls',
      description: 'Implementation of 421+ NIST 800-53 security controls'
    },
    {
      icon: <AssessmentIcon sx={{ color: 'primary.main' }} />,
      title: 'Continuous Monitoring',
      description: 'Real-time security monitoring with automated compliance reporting'
    },
    {
      icon: <VerifiedUserIcon sx={{ color: 'primary.main' }} />,
      title: 'Third-Party Assessment',
      description: 'Regular audits by FedRAMP-approved 3PAO organizations'
    }
  ];

  const useCases = [
    {
      title: 'Federal Agency Deployment',
      description: 'Rapid deployment for federal agencies requiring FedRAMP High authorization'
    },
    {
      title: 'Sensitive Data Management',
      description: 'Secure handling of CUI and other sensitive government information'
    },
    {
      title: 'Multi-Agency Collaboration',
      description: 'Enable secure data sharing across federal agencies with confidence'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back to About Page
      </Button>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Core Capability tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '.MuiTab-root': {
            color: 'primary.main',
            minHeight: 64,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: 'primary.main',
            height: 3
          }
        }}
      >
        <Tab label="High Performance" icon={<SpeedIcon />} iconPosition="start" />
        <Tab label="Zero Trust" icon={<ShieldIcon />} iconPosition="start" />
        <Tab label="FedRAMP Ready" icon={<CloudDoneIcon />} iconPosition="start" />
        <Tab label="508 Compliant" icon={<AccessibilityIcon />} iconPosition="start" />
        <Tab label="Scalable" icon={<TrendingUpIcon />} iconPosition="start" />
        <Tab label="No Lock-In" icon={<LockOpenIcon />} iconPosition="start" />
      </Tabs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CloudDoneIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            FedRAMP Ready
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Built for FedRAMP High authorization with government-approved cloud services
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography paragraph sx={{ fontSize: '0.95rem' }}>
              E-Unify is architected from the ground up to meet FedRAMP High requirements, the most stringent cloud security authorization level for federal systems. Our platform is built on FedRAMP-authorized infrastructure and implements comprehensive security controls to protect sensitive government data.
            </Typography>
            <Typography sx={{ fontSize: '0.95rem' }}>
              By leveraging FedRAMP-authorized cloud services and maintaining continuous compliance, E-Unify accelerates the authorization process for federal agencies, reducing time-to-deployment while ensuring the highest levels of security and compliance.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Key Benefits
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Compliance Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Common Use Cases
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {useCases.map((useCase, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 3, borderLeft: '4px solid #003366' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {useCase.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {useCase.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: 'primary.main',
          color: 'white'
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Deploy with Confidence
        </Typography>
        <Typography variant="body1" paragraph>
          Learn how E-Unify's FedRAMP-ready architecture accelerates your authorization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            '&:hover': {
              bgcolor: '#f0f0f0'
            }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default FedRAMPReady;
